import type { AuthDataStrategy } from "../auth/types";
import type {
  CreateUrqlClientArgs,
  UrqlClient,
} from "../remote/createUrqlClient";
import type { IAuthHandler } from "./authHandler";
import type { LoggerLevels, LoggerType } from "./log";
import type {
  AlaffiaClientModule,
  AlaffiaClientModuleArgs,
} from "./modules/modules";

import { createUrqlClient } from "../remote/createUrqlClient";
import { AuthHandler } from "./authHandler";
import { ClientError } from "./error";
import { getLogger } from "./log";
import { ApiModule } from "./modules/apiModule";
import { AprDrgModule } from "./modules/aprDrgModule";
import { AuditorUsersModule } from "./modules/auditorUsersModule";
import { AuditRulesModule } from "./modules/auditRulesModule";
import { AuditsModule } from "./modules/auditsModule";
import { AuthModule } from "./modules/authModule";
import { BatchClaimsModule } from "./modules/batchClaimsModule";
import { CasesModule } from "./modules/casesModule";
import { ClaimsModule } from "./modules/claimsModule";
import { DashboardModule } from "./modules/dashboardModule";
import { DrgModule } from "./modules/drgModule";
import { FilesModule } from "./modules/filesModule";
import { InsurersModule } from "./modules/insurersModule";
import { LifecycleRolesModule } from "./modules/lifecycleRolesModule";
import { ProcedureCodesModule } from "./modules/procedureCodesModule";
import { ProvidersModule } from "./modules/providersModule";
import { QueuesModule } from "./modules/queuesModule";
import { ReportsModule } from "./modules/reportsModule";
import { RevCodesModule } from "./modules/revCodesModule";
import { SsoDomainIdentityProvidersModule } from "./modules/ssoDomainIdentityProvidersModule";
import { UsersModule } from "./modules/usersModule";

export type AlaffiaClientExtraModules = Record<string, AlaffiaClientModule>;

/**
 * Constructor args for Alaffia Client
 */
export interface AlaffiaClientArgs<
  TExtraModules extends AlaffiaClientExtraModules = Record<string, never>,
> {
  apiUrl: string;
  alafedApiUrl?: string;
  environment?: "tonik-preview" | "alafed";
  graphqlOptions?: Omit<CreateUrqlClientArgs, "apiUrl" | "auth">;
  logLevel?: LoggerLevels;
  authData?: AuthDataStrategy;
  extraModules?: {
    [key in keyof TExtraModules]: new (
      args: AlaffiaClientModuleArgs,
    ) => TExtraModules[key];
  };
}

const defaultsCtorArgs = {
  logLevel: "WARN",
} satisfies Partial<AlaffiaClientArgs>;

/**
 * The Alaffia client sdk for remote (http) access
 */
export class AlaffiaClient<
  TExtraModules extends AlaffiaClientExtraModules = Record<string, never>,
> {
  private apiUrl: string;
  private alafedApiUrl?: string;
  private environment: "tonik-preview" | "alafed";
  private graphqlOptions?: Omit<CreateUrqlClientArgs, "apiUrl" | "auth">;
  private gqlClient: UrqlClient;
  private logger: LoggerType;
  private authData?: AuthDataStrategy;
  private authHandler: IAuthHandler;

  public readonly api: ApiModule;
  public readonly lifecycleRoles: LifecycleRolesModule;
  public readonly audits: AuditsModule;
  public readonly aprDrg: AprDrgModule;
  public readonly auditRules: AuditRulesModule;
  public readonly auditorUsers: AuditorUsersModule;
  public readonly auth: AuthModule;
  public readonly batchClaims: BatchClaimsModule;
  public readonly cases: CasesModule;
  public readonly claims: ClaimsModule;
  public readonly drg: DrgModule;
  public readonly extraModules: TExtraModules;
  public readonly files: FilesModule;
  public readonly dashboard: DashboardModule;
  public readonly insurers: InsurersModule;
  public readonly procedureCodes: ProcedureCodesModule;
  public readonly providers: ProvidersModule;
  public readonly queues: QueuesModule;
  public readonly reports: ReportsModule;
  public readonly revCodes: RevCodesModule;
  public readonly ssoDomainIdentityProviders: SsoDomainIdentityProvidersModule;
  public readonly users: UsersModule;

  constructor(args: AlaffiaClientArgs<TExtraModules>) {
    const processedArgs = this.processCtorArgs(args);
    this.apiUrl = processedArgs.apiUrl;
    this.alafedApiUrl = processedArgs.alafedApiUrl;
    this.environment = processedArgs.environment ?? "tonik-preview";
    this.authData = processedArgs.authData;
    this.graphqlOptions = processedArgs.graphqlOptions;
    this.logger = getLogger("AlaffiaClient", processedArgs.logLevel);
    this.authHandler = new AuthHandler({
      url: this.apiUrl,
      authData: this.authData,
      onLoginStatusChange: (loginStatus) => {
        const eventType = loginStatus.data?.type;
        if (
          eventType === "LogoutEvent" ||
          eventType === "SessionExpiredEvent"
        ) {
          this.gqlClient = this.createGqlClient(processedArgs.graphqlOptions);
        }
      },
    });

    this.gqlClient = this.createGqlClient(processedArgs.graphqlOptions);

    const moduleArgs = {
      apiUrl: this.apiUrl,
      getGqlClient: this.getGqlClient,
      logger: this.logger,
      environment: this.environment,
    };

    this.api = new ApiModule({
      ...moduleArgs,
      authHandler: this.authHandler,
    });

    this.aprDrg = new AprDrgModule(moduleArgs);

    this.auth = new AuthModule({
      ...moduleArgs,
      authHandler: this.authHandler,
    });

    this.lifecycleRoles = new LifecycleRolesModule(moduleArgs);
    this.audits = new AuditsModule(moduleArgs);
    this.auditRules = new AuditRulesModule(moduleArgs);
    this.auditorUsers = new AuditorUsersModule(moduleArgs);
    this.batchClaims = new BatchClaimsModule(moduleArgs);
    this.cases = new CasesModule(moduleArgs);
    this.claims = new ClaimsModule(moduleArgs);
    this.drg = new DrgModule(moduleArgs);
    this.files = new FilesModule(moduleArgs);
    this.dashboard = new DashboardModule(moduleArgs);
    this.insurers = new InsurersModule(moduleArgs);
    this.procedureCodes = new ProcedureCodesModule(moduleArgs);
    this.providers = new ProvidersModule(moduleArgs);
    this.queues = new QueuesModule(moduleArgs);
    this.reports = new ReportsModule(moduleArgs);
    this.revCodes = new RevCodesModule(moduleArgs);
    this.ssoDomainIdentityProviders = new SsoDomainIdentityProvidersModule(
      moduleArgs,
    );
    this.users = new UsersModule(moduleArgs);

    const extraModules = {} as TExtraModules;
    for (const key in processedArgs.extraModules) {
      const module = processedArgs.extraModules[key];
      extraModules[key] = new module(moduleArgs);
    }
    this.extraModules = extraModules;
  }

  private createGqlClient = (
    graphqlOptions?: Omit<CreateUrqlClientArgs, "apiUrl" | "auth">,
    apiUrl: string = this.apiUrl,
  ) => {
    const { cacheExchangeOptions, ...restGraphqlOptions } =
      graphqlOptions ?? {};

    return createUrqlClient({
      cacheExchangeOptions,
      ...restGraphqlOptions,
      apiUrl: apiUrl + "/graphql",
      auth: this.authHandler,
    });
  };

  private getGqlClient = (environment?: "tonik-preview" | "alafed") => {
    if (environment === "alafed" && this.environment !== "alafed") {
      return this.createGqlClient(
        this.graphqlOptions,
        this.alafedApiUrl ?? this.apiUrl,
      );
    }
    return this.gqlClient;
  };

  // Example of an internal method to process constructor arguments
  private processCtorArgs(
    args: AlaffiaClientArgs<TExtraModules>,
  ): AlaffiaClientArgs<TExtraModules> {
    const argsWithDefaults = {
      ...defaultsCtorArgs,
      ...args,
    };
    if (!argsWithDefaults.apiUrl) {
      throw new Error("API Url is required to create Alaffia client");
    }
    if (argsWithDefaults.apiUrl.endsWith("/")) {
      argsWithDefaults.apiUrl = argsWithDefaults.apiUrl.slice(0, -1);
    }
    return argsWithDefaults;
  }

  private clientError = (contextMessage: string, error: any) => {
    const originalMessage = error?.message ?? error.toString(); // todo include cause?
    const msg = `${contextMessage}: ${originalMessage}`;
    this.logger.error(msg, { error });
    return new ClientError(msg, { error });
  };

  getClientError = () => {
    return this.clientError;
  };
}
